import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Section";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";

const PATENTS = [
  {
    id: "US 11,458,215 B1",
    title: "Wearable Sanitizing Dispenser",
    granted: "Oct 2022",
    role: "Foundational wearable dispensing architecture",
    status: "ACTIVE · UTILITY",
  },
  {
    id: "US 11,717,063 B1",
    title: "Wearable Dispenser for Applicable Solutions",
    granted: "Aug 2023",
    role: "Expanded applicable solutions and platform embodiments",
    status: "ACTIVE · UTILITY",
  },
  {
    id: "US 12,285,084 B1",
    title: "Wearable Dispenser for Applicable Solutions",
    granted: "Apr 2025",
    role: "Continuation including sensor integration claim coverage",
    status: "ACTIVE · UTILITY",
  },
  {
    id: "US D993,609 S1",
    title: "Wrist Wearable Liquid Dispenser",
    granted: "Aug 2023",
    role: "Protected ornamental design",
    status: "ACTIVE · DESIGN",
  },
];

const EVOLUTION = [
  { year: "2022", id: "US 11,458,215", note: "Foundational wearable dispensing architecture." },
  { year: "2023", id: "US 11,717,063", note: "Expanded applicable solutions and platform embodiments." },
  { year: "2025", id: "US 12,285,084", note: "Continuation with additional structural and sensor claim coverage." },
  { year: "DESIGN", id: "US D993,609", note: "Protected product appearance." },
];

const EXPANSION = [
  { title: "Core Dispensing Architecture", desc: "Refillable reservoir, resilient diaphragm, and controlled outlet architecture." },
  { title: "Multiple Formulations", desc: "Liquids, lotions, gels, fragrances, sunscreen, repellents, and other skin-applicable solutions." },
  { title: "Multiple Chamber Architectures", desc: "Single and dual reservoir configurations for separate formulations." },
  { title: "Device-Attached Delivery", desc: "Embodiments extending dispensing beyond the wrist, including phone-attached configurations." },
  { title: "Body Sensor Integration", desc: "Issued claim protection incorporating body sensing into the dispensing apparatus." },
  { title: "Connected Wearable Systems", desc: "Digital displays, processors, alerts, location, and connectivity architectures described within the patent family." },
];

const COMMERCIAL = [
  { title: "Personal Care", desc: "Sunscreen, skincare, moisturizer, fragrance." },
  { title: "Protection", desc: "Repellent, sanitizer, sun protection." },
  { title: "Connected Wearables", desc: "Sensors, alerts, digital displays, contextual intelligence." },
  { title: "Device Integration", desc: "Wrist-worn, phone-attached, and future connected formats." },
];

const PATENTS_PRECURSOR = [
  <>Four <span className="text-accent">issued</span> U.S. patents.</>,
  <>Refillable, <span className="text-accent">wearable</span> dispensing.</>,
  <>From sun care to <span className="text-accent">smart wearables</span>.</>,
  <>An early position in <span className="text-accent">biometric wearables</span>.</>,
];

function PatentsPrecursor() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % PATENTS_PRECURSOR.length), 3800);
    return () => clearInterval(t);
  }, []);
  return (
    <section
      className="border-t border-zinc-800 bg-[#0a0a0a] overflow-hidden"
      data-testid="patents-precursor"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent block mb-8">
          The IP Position
        </span>
        <div className="relative h-[130px] sm:h-[150px] md:h-[170px]">
          <AnimatePresence>
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -16, filter: "blur(10px)" }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0 flex items-center font-heading text-3xl sm:text-5xl md:text-6xl font-light tracking-tight leading-[1.05]"
              data-testid="patents-precursor-text"
            >
              <span className="max-w-4xl">{PATENTS_PRECURSOR[i]}</span>
            </motion.p>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

const PATENT_HERO = [
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/hbq1nxvw_Magenta%20Band%20Product%20Shot%20with%20CAD%20Blueprints.jpg",
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/tzxa64z9_patent%20drawing.webp",
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/ugtp75el_WearSpray-%20Cad%20Design%20Pic.jpg",
];

// Real figures from the issued design patent, US D993,609 S (USPTO public record).
const PATENT_FIGURES = [
  {
    key: "fig-02",
    tab: "FIG. 2",
    sheet: "02",
    img: "/patents/fig-02-blueprint.jpg",
    view: "Perspective View",
    caption: "Perspective view of the assembled band — the dispensing ports and reservoir cap as they sit on the wrist.",
    hotspots: [
      { x: 59, y: 44, label: "Reservoir Cap" },
      { x: 27, y: 46, label: "Strap Loop" },
      { x: 43, y: 64, label: "Port Array" },
      { x: 49, y: 33, label: "Band Shell" },
    ],
  },
  {
    key: "fig-05",
    tab: "FIG. 5",
    sheet: "05",
    img: "/patents/fig-05-blueprint.jpg",
    view: "Exploded Perspective",
    caption: "Exploded perspective view — the wristband shell, dispensing-port strip, and dual end caps.",
    hotspots: [
      { x: 18, y: 46, label: "End Cap" },
      { x: 58, y: 24, label: "Port Array" },
      { x: 42, y: 50, label: "Band Shell" },
      { x: 68, y: 53, label: "Ribbed Cap" },
      { x: 68, y: 90, label: "Strap Buckle" },
    ],
  },
  {
    key: "fig-07",
    tab: "FIG. 7",
    sheet: "07",
    img: "/patents/fig-07-blueprint.jpg",
    view: "Top Plan View",
    caption: "Top plan view of the band, shown with two alternate cap styles above and below.",
    hotspots: [
      { x: 50, y: 20, label: "Port Column" },
      { x: 50, y: 63, label: "Center Opening" },
      { x: 23, y: 63, label: "Ribbed Cap" },
      { x: 76, y: 63, label: "End Ring" },
      { x: 50, y: 93, label: "Buckle" },
    ],
  },
  {
    key: "fig-08",
    tab: "FIG. 8",
    sheet: "08",
    img: "/patents/fig-08-blueprint.jpg",
    view: "Side Elevation, Exploded",
    caption: "Side elevation, exploded, tracing the band profile end to end.",
    hotspots: [
      { x: 9, y: 52, label: "End Cap" },
      { x: 19, y: 52, label: "Port Strip" },
      { x: 33, y: 50, label: "Band Shell" },
      { x: 65, y: 30, label: "Strap Arm" },
      { x: 85, y: 90, label: "Buckle Tip" },
    ],
  },
  {
    key: "fig-10",
    tab: "FIG. 10",
    sheet: "10",
    img: "/patents/fig-10-blueprint.jpg",
    view: "Detail View",
    caption: "Detail view of the end cap, port strip, and shell assembly.",
    hotspots: [
      { x: 48, y: 16, label: "End Cap" },
      { x: 48, y: 26, label: "Port Strip" },
      { x: 26, y: 31, label: "Pin Detail" },
      { x: 48, y: 55, label: "Port Column" },
    ],
  },
];

function PatentHotspot({ x, y, label, figKey }) {
  return (
    <div
      className="absolute -translate-x-1/2 -translate-y-1/2 group z-10"
      style={{ left: `${x}%`, top: `${y}%` }}
      data-testid={`patent-hotspot-${figKey}-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full rounded-full bg-accent/60 animate-ping" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent shadow-[0_0_8px_rgba(0,229,255,0.9)]" />
      </span>
      <span className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 whitespace-nowrap border border-accent/50 bg-black px-2 py-1 font-mono text-[10px] tracking-[0.12em] uppercase text-accent opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        {label}
      </span>
    </div>
  );
}

function PatentBlueprint() {
  const [active, setActive] = useState(0);
  const fig = PATENT_FIGURES[active];

  return (
    <section className="border-t border-zinc-800 bg-black overflow-hidden" data-testid="patents-blueprint-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">US Design Patent D993,609 S</span>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight mt-4 max-w-3xl leading-[1.08]">
            The actual filing.{" "}
            <span className="text-accent">No embellishment needed.</span>
          </h2>
          <p className="font-body text-lg text-zinc-400 leading-relaxed mt-6 max-w-2xl">
            Issued by the United States Patent and Trademark Office on August 1,
            2023, protecting the ornamental design of the WearSpray wearable.
            What follows is the real certificate and the real drawings, pulled
            straight from the public record.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 mt-14">
          {/* CERTIFICATE */}
          <Reveal className="lg:col-span-4" delay={0.05}>
            <motion.div
              className="relative border border-zinc-800 bg-[#0d0d0d] overflow-hidden group h-[420px] sm:h-[480px] md:h-[520px] lg:h-[580px] flex flex-col"
              data-testid="patent-certificate-card"
              whileHover={{ y: -4 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="absolute top-4 left-4 z-10 flex items-center gap-2 border border-accent/40 bg-black/70 px-3 py-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent">Issued · USPTO</span>
              </div>
              <div className="flex-1 flex items-center justify-center p-6 pt-14">
                <img loading="lazy"
                  src="/patents/certificate.png"
                  alt="Official United States design patent certificate for D993,609 S, signed by Katherine Kelly Vidal, Director of the USPTO"
                  className="max-h-full max-w-full object-contain drop-shadow-[0_10px_40px_rgba(0,0,0,0.7)] transition-transform duration-700 group-hover:scale-[1.03]"
                  data-testid="patent-certificate-image"
                />
              </div>
              <div className="px-6 pb-6">
                <p className="font-mono text-xs text-accent tracking-[0.15em] uppercase">US D993,609 S</p>
                <p className="font-heading text-lg text-white mt-2 leading-snug">Signed by Katherine Kelly Vidal</p>
                <p className="font-body text-sm text-zinc-400 mt-1">Director, U.S. Patent and Trademark Office · Aug 1, 2023</p>
              </div>
            </motion.div>
          </Reveal>

          {/* BLUEPRINT VIEWER */}
          <Reveal className="lg:col-span-8" delay={0.1}>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="patent-fig-tabs">
              {PATENT_FIGURES.map((f, i) => (
                <button
                  key={f.key}
                  data-testid={`patent-fig-tab-${f.key}`}
                  onClick={() => setActive(i)}
                  className={`font-mono text-xs tracking-[0.15em] uppercase px-4 py-2.5 border transition-colors ${
                    active === i
                      ? "border-accent text-accent bg-accent/10"
                      : "border-zinc-800 text-zinc-500 hover:text-zinc-300 hover:border-zinc-700"
                  }`}
                >
                  {f.tab}
                </button>
              ))}
            </div>

            <div
              className="relative border border-zinc-800 bg-[#050505] h-[380px] md:h-[520px] overflow-hidden"
              data-testid="patent-fig-viewer"
            >
              {/* blueprint grid */}
              <div
                className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(0,229,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,255,0.5) 1px, transparent 1px)",
                  backgroundSize: "36px 36px",
                }}
              />
              {/* corner brackets */}
              <span className="absolute top-3 left-3 h-5 w-5 border-t border-l border-accent/70 pointer-events-none" />
              <span className="absolute top-3 right-3 h-5 w-5 border-t border-r border-accent/70 pointer-events-none" />
              <span className="absolute bottom-3 left-3 h-5 w-5 border-b border-l border-accent/70 pointer-events-none" />
              <span className="absolute bottom-3 right-3 h-5 w-5 border-b border-r border-accent/70 pointer-events-none" />
              {/* scan line */}
              <div className="absolute inset-x-0 top-0 h-px bg-accent/70 shadow-[0_0_12px_rgba(0,229,255,0.8)] animate-hud-scan pointer-events-none" />

              <AnimatePresence mode="wait">
                <motion.div
                  key={fig.key}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 flex items-center justify-center p-6 md:p-10"
                >
                  <div className="relative h-full inline-block drop-shadow-[0_0_36px_rgba(46,235,255,0.35)]">
                    <img loading="lazy"
                      src={fig.img}
                      alt={`WearSpray design patent ${fig.tab}, ${fig.view}`}
                      className="h-full w-auto object-contain block"
                      data-testid="patent-fig-image"
                    />
                    {fig.hotspots.map((h) => (
                      <PatentHotspot key={h.label} {...h} figKey={fig.key} />
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="absolute bottom-3 left-4 font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500 z-10">
                Sheet {fig.sheet} of 11
              </div>
              <div className="absolute bottom-3 right-4 font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500 z-10">
                US D993,609 S
              </div>
            </div>
            <p className="font-body text-sm text-zinc-400 leading-relaxed mt-4" data-testid="patent-fig-caption">
              <span className="text-accent font-mono text-xs tracking-[0.1em] uppercase mr-2">{fig.tab}</span>
              {fig.caption}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Patents() {
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setHeroIdx((p) => (p + 1) % PATENT_HERO.length), 5000);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="patents-page">
      <SEO {...PAGE_SEO["/patents"]} path="/patents" />
      {/* HERO */}
      <section
        className="relative min-h-[88vh] w-full overflow-hidden bg-black"
        data-testid="patents-hero-section"
      >
        {PATENT_HERO.map((src, i) => (
          <motion.div
            key={src}
            className="absolute inset-0"
            initial={false}
            animate={{ opacity: i === heroIdx ? 1 : 0 }}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
          >
            <img loading="lazy"
              src={src}
              alt="WearSpray patent and CAD engineering"
              className="w-full h-full object-cover"
              style={{ objectPosition: "center", filter: "brightness(0.98) contrast(1.02)", transform: "translateX(16%) scale(1.36)" }}
            />
          </motion.div>
        ))}
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />

        <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center pt-24">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="font-mono text-xs tracking-[0.25em] uppercase text-accent mb-6"
            data-testid="patents-hero"
          >
            Intellectual Property
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.02] max-w-4xl"
          >
            Defensible IP for Wearable and{" "}
            <span className="text-accent">Device-Attached Delivery.</span>
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-8"
            data-testid="patents-pillars"
          >
            {["AI Skin Intelligence", "Formulation Science", "Sensing", "Immediate Application"].map((t, i) => (
              <span key={t} className="flex items-center gap-3">
                {i > 0 && <span className="text-zinc-600 font-mono text-xs">+</span>}
                <span className="font-mono text-xs md:text-sm tracking-[0.18em] uppercase text-[#e6c068]">{t}</span>
              </span>
            ))}
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.24 }}
            className="font-body text-lg md:text-xl text-zinc-300 mt-8 max-w-2xl leading-relaxed"
            data-testid="patents-problem-statement"
          >
            Most people know what they should do for their skin. Reapply
            sunscreen. Use moisturizer. Protect themselves outdoors. Stay
            consistent.{" "}
            <span className="text-accent">
              The challenge is acting at the right moment.
            </span>
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="font-body text-lg md:text-xl text-white mt-6 max-w-2xl leading-relaxed"
            data-testid="patents-ai-statement"
          >
            WearSpray is designed to close that gap. Its AI intelligence
            layer interprets environmental, behavioral, and biometric
            information, prompts the user when application may be needed,
            and connects that insight to a chosen personal care formulation
            carried directly on the wrist.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.38 }}
            className="font-body text-base text-zinc-500 mt-5 max-w-2xl leading-relaxed"
          >
            WearSpray holds an issued United States patent family covering
            refillable wearable dispensing, device integrated delivery,
            sensor and smartwatch integration, digital displays, multiple
            formulations, and connected wearable applications.
          </motion.p>
          <div className="flex gap-2 mt-10" data-testid="patents-hero-progress">
            {PATENT_HERO.map((src, i) => (
              <span
                key={src}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === heroIdx ? "w-10 bg-accent" : "w-5 bg-white/30"
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      <PatentsPrecursor />

      {/* PATENT TABLE, with Strategic Role */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="patents-table">
        <div className="border border-zinc-800 bg-[#0d0d0d]">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-zinc-800 bg-[#121212]">
            <span className="col-span-3 font-mono text-xs tracking-[0.15em] uppercase text-zinc-500">Patent No.</span>
            <span className="col-span-3 font-mono text-xs tracking-[0.15em] uppercase text-zinc-500">Title</span>
            <span className="col-span-4 font-mono text-xs tracking-[0.15em] uppercase text-zinc-500">Strategic Role</span>
            <span className="col-span-2 font-mono text-xs tracking-[0.15em] uppercase text-zinc-500 text-right">Granted</span>
          </div>

          {PATENTS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06}>
              <div
                data-testid={`patent-row-${i}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-6 border-b border-zinc-800 last:border-b-0 hover:bg-[#141414] transition-colors group"
              >
                <span className="md:col-span-3 font-mono text-sm text-accent">{p.id}</span>
                <span className="md:col-span-3 font-heading text-base md:text-lg text-white">{p.title}</span>
                <span className="md:col-span-4 font-body text-sm text-zinc-400 leading-snug">{p.role}</span>
                <span className="md:col-span-2 md:text-right font-mono text-sm text-zinc-500">{p.granted}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <PatentBlueprint />

      {/* PATENT FAMILY EVOLUTION */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="patents-evolution">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">Patent Family Evolution</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight mt-4 max-w-3xl">
              Not one filing. A{" "}
              <span className="text-accent">continuing IP strategy.</span>
            </h2>
            <p className="font-body text-zinc-400 leading-relaxed mt-6 max-w-2xl">
              Each patent builds on the last, a family that kept generating
              issued protection as the platform expanded from the wrist toward
              sensing and connected devices.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800 mt-12">
            {EVOLUTION.map((e, i) => (
              <Reveal key={e.id} delay={i * 0.08}>
                <div className="bg-[#121212] p-8 h-full relative" data-testid={`patents-evolution-${i}`}>
                  <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">{e.year}</span>
                  <p className="font-heading text-xl text-white mt-4 tracking-tight">{e.id}</p>
                  <p className="font-body text-sm text-zinc-400 leading-relaxed mt-4">{e.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLAIM-LEVEL PROTECTION, Claim 13 sensor integration */}
      <section className="border-t border-zinc-800 bg-black overflow-hidden" data-testid="patents-claim13">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-4">
            <div className="relative border border-accent/30 bg-[#0c0c0c] p-10 text-center overflow-hidden">
              <div className="absolute -inset-8 blur-3xl opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.25), transparent 65%)" }} />
              <div className="relative">
                <span className="font-mono text-xs tracking-[0.3em] uppercase text-accent block mb-4">Claim 13</span>
                <p className="font-heading font-light tracking-tighter text-accent leading-none" style={{ fontSize: "9rem" }}>13</p>
                <span className="font-mono text-xs tracking-[0.18em] uppercase text-zinc-500 block mt-4">Issued Claim Coverage</span>
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-8">
            <Reveal>
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">Claim-Level Protection</span>
              <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight mt-4 leading-[1.08]">
                Patented Sensor Integration.
              </h2>
              <p className="font-mono text-sm text-zinc-400 mt-4">U.S. Patent No. 12,285,084 B1</p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-body text-lg text-zinc-200 leading-relaxed mt-8 max-w-2xl">
                Claim 13 expressly covers a dispensing apparatus incorporating at
                least one body sensor, including heart rate, blood pressure,
                sweat, body temperature, analyte, and blood oxygen sensing.
              </p>
              <p className="font-body text-zinc-400 leading-relaxed mt-5 max-w-2xl border-l-2 border-accent pl-5">
                The significance is not the sensor alone. It is the patented
                combination of sensing capability with the WearSpray dispensing
                architecture.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DEVICE-ATTACHED PLATFORM, from wrist to phone */}
      <section className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="patents-device-attached">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">Device-Attached Platform</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight mt-4 max-w-3xl leading-[1.08]">
              From the wrist to{" "}
              <span className="text-accent">the phone.</span>
            </h2>
            <p className="font-body text-lg text-zinc-300 leading-relaxed mt-8 max-w-3xl">
              The WearSpray patent specification expressly describes embodiments
              attached to the back of a cell phone, extending the dispensing
              architecture beyond the wrist.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12 max-w-3xl">
            <Reveal>
              <div className="border border-accent/30 bg-[#101010] p-6 h-full">
                <span className="font-mono text-xs tracking-[0.18em] uppercase text-accent block mb-3">Body Sensor</span>
                <p className="font-heading text-lg text-white">Issued claim coverage</p>
                <p className="font-body text-sm text-zinc-500 mt-2">Claim 13 · US 12,285,084 B1</p>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="border border-zinc-800 bg-[#101010] p-6 h-full">
                <span className="font-mono text-xs tracking-[0.18em] uppercase text-zinc-400 block mb-3">Phone Attachment</span>
                <p className="font-heading text-lg text-white">Patent-described embodiment</p>
                <p className="font-body text-sm text-zinc-500 mt-2">Disclosed in the specification</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PLATFORM EXPANSION, technical IP capabilities */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="patents-expansion">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Reveal>
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">Platform Expansion</span>
              <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight mt-4">
                Four layers of protection. One architecture.
              </h2>
              <p className="font-body text-zinc-400 leading-relaxed mt-6">
                Every capability below is grounded in the issued patent family and
                its supporting disclosures, not marketing language, but the
                defensible IP that underpins the platform.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
              {EXPANSION.map((e, i) => (
                <Reveal key={e.title} delay={i * 0.05}>
                  <div className="bg-[#121212] p-6 h-full hover:bg-[#161616] transition-colors" data-testid={`patents-capability-${i}`}>
                    <span className="font-mono text-xs text-accent tracking-[0.15em]">
                      0{i + 1} · {e.title.toUpperCase()}
                    </span>
                    <p className="font-body text-sm text-zinc-300 mt-3 leading-relaxed">{e.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMMERCIAL RELEVANCE */}
      <section className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="patents-commercial">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">Commercial Relevance</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight mt-4 max-w-3xl leading-[1.08]">
              One platform.{" "}
              <span className="text-accent">Multiple commercial paths.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {COMMERCIAL.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <div className="border border-zinc-800 bg-[#121212] p-8 h-full hover:border-zinc-700 transition-colors" data-testid={`patents-commercial-${i}`}>
                  <span className="font-mono text-xs tracking-[0.15em] uppercase text-accent block mb-4">{c.title}</span>
                  <p className="font-body text-zinc-300 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING STATEMENT */}
      <section className="border-t border-zinc-800 bg-black" data-testid="patents-closing">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-28 md:py-36 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.1]">
              One patented architecture.{" "}
              <span className="text-accent">An entire family of possibilities.</span>
            </h2>
            <p className="font-body text-zinc-400 leading-relaxed mt-8 max-w-2xl mx-auto">
              WearSpray is not protected around a single product configuration.
              The issued patent family and supporting disclosures establish a
              broader dispensing architecture spanning wearable delivery, multiple
              formulations, device-attached formats, digital integration, and body
              sensing.
            </p>
            <div className="flex flex-wrap gap-4 justify-center mt-12">
              <Link
                to="/products"
                data-testid="patents-cta-products"
                className="font-body text-sm font-medium text-black bg-accent px-8 py-4 hover:-translate-y-0.5 transition-transform"
              >
                Explore the Products
              </Link>
              <Link
                to="/contact"
                data-testid="patents-cta-partner"
                className="font-body text-sm font-medium text-white border border-zinc-700 px-8 py-4 hover:border-accent hover:text-accent transition-colors"
              >
                Partner With WearSpray
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LEGAL NOTE */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="patents-legal">
        <Reveal>
          <p className="font-mono text-xs text-zinc-500 leading-relaxed max-w-3xl">
            Patent information is provided for informational purposes based on
            publicly available records. Patent scope, enforceability, and legal
            interpretation should be confirmed by qualified patent counsel.
          </p>
        </Reveal>
      </section>
    </div>
  );
}
